import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  const article = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading text-foreground-900 font-semibold mb-2">文章未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">您要查看的文章不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id)
    .slice(0, 3);

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <article>
          <div className="relative w-full h-48 md:h-64 overflow-hidden">
            <img
              src={article.coverImage}
              alt={article.title}
              title={article.title}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
          </div>

          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 py-4 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <Link to={`/category/${article.categorySlug}`} className="hover:text-primary-600 transition-colors whitespace-nowrap">
                {article.category}
              </Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-900 font-medium line-clamp-1">{article.title}</span>
            </nav>

            <header className="mb-8">
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading text-foreground-900 font-bold mb-4">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line text-sm"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line text-sm"></i>
                  {article.publishDate}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line text-sm"></i>
                  {(article.readCount / 10000).toFixed(1)}万 阅读
                </span>
                <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-50 text-primary-700">
                  {article.category}
                </span>
              </div>
            </header>

            <div className="flex flex-wrap gap-2 mb-8">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700"
                >
                  #{tag}
                </span>
              ))}
            </div>

            <div className="prose prose-base md:prose-lg max-w-none mb-16">
              {article.content.split('\n').map((paragraph, index) => {
                if (paragraph.trim() === '') return null;
                if (paragraph.startsWith('第') && paragraph.includes('：')) {
                  return (
                    <h2 key={index} className="text-xl font-heading text-foreground-900 font-semibold mt-8 mb-3">
                      {paragraph}
                    </h2>
                  );
                }
                if (paragraph.startsWith('误区') || paragraph.match(/^第[一二三四五六七八九十]/)) {
                  return (
                    <h3 key={index} className="text-lg font-heading text-foreground-800 font-medium mt-6 mb-2">
                      {paragraph}
                    </h3>
                  );
                }
                return (
                  <p key={index} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                    {paragraph}
                  </p>
                );
              })}
            </div>

            {relatedArticles.length > 0 && (
              <section className="border-t border-background-200/70 pt-10 mb-16">
                <h2 className="text-xl font-heading text-foreground-900 font-bold mb-6">
                  相关推荐
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  {relatedArticles.map((rel) => (
                    <Link
                      key={rel.id}
                      to={`/article/${rel.id}`}
                      className="group bg-background-100 rounded-lg border border-background-200/70 overflow-hidden transition-all duration-300"
                    >
                      <div className="relative w-full h-36 overflow-hidden">
                        <img
                          src={rel.coverImage}
                          alt={rel.title}
                          title={rel.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="text-sm font-heading text-foreground-900 font-semibold mb-1 line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {rel.title}
                        </h3>
                        <span className="text-xs text-foreground-500">{rel.publishDate}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>

        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            author: { '@type': 'Person', name: article.author },
            datePublished: article.publishDate,
            image: article.coverImage,
            articleSection: article.category,
            keywords: article.tags.join(', '),
          })}
        </script>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}